import type { OwnedPokemon } from '~/stores/useInventoryStore'
import { HATCH_DAMAGE, type DaycareSlot } from '~/stores/useDaycareStore'
import { RARITY_SORT_ORDER } from '~/data/gacha'
import type { Rarity } from '~/data/gacha'
export {
  createDaycareDepositId,
  hasDaycareDepositId,
  mergeDaycareSlotsPreservingProgress,
  reconcileDaycareSlotStarsAndDamage,
} from '../../../shared/daycare_merge'

export interface DaycareQueueEntry {
  slug: string
  nameFr: string
  nameEn: string
  rarity: Rarity
  isShiny: boolean
  stars: number
  cosmeticFormSlug?: string | null
}

export interface DaycareCandidateOptions {
  shinyGoal?: boolean
  hideOwnedShiny?: boolean
  /** Si true, n’inventorie pas les Pokémon encore en équipe (remplissage auto pension). */
  excludeTeamMembers?: boolean
  /**
   * Ordre de secours pour le remplissage auto (sans file utilisateur) :
   * légendaires d’abord, plus d’étoiles, priorité aux espèces avec shiny incomplet puis au shiny le plus avancé, puis slug.
   */
  daycareAutoPickOrder?: boolean
}

const RARITY_ORDER = RARITY_SORT_ORDER

function slotKey(slug: string, isShiny: boolean) {
  return `${slug}:${isShiny ? 1 : 0}`
}

export function hasOwnedShiny(collection: OwnedPokemon[], slug: string): boolean {
  return collection.some((p) => p.slug === slug && p.isShiny)
}

export function hasOwnedShinyFiveStars(
  collection: OwnedPokemon[],
  slug: string,
  maxStars: number
): boolean {
  return collection.some((p) => p.slug === slug && p.isShiny && p.stars >= maxStars)
}

/**
 * Nombre d'étoiles du meilleur shiny possédé pour cette espèce (0 si aucun shiny possédé).
 * Sert à afficher, sur un Pokémon en pension, l'avancement de son alter ego shiny.
 */
export function ownedShinyStars(collection: OwnedPokemon[], slug: string): number {
  let best = 0
  for (const p of collection) {
    if (p.slug === slug && p.isShiny && p.stars > best) best = p.stars
  }
  return best
}

function compareOwnedPokemon(a: OwnedPokemon, b: OwnedPokemon): number {
  return (
    b.stars - a.stars ||
    (RARITY_ORDER[a.rarity] ?? 99) - (RARITY_ORDER[b.rarity] ?? 99) ||
    b.level - a.level ||
    a.slug.localeCompare(b.slug) ||
    a.id - b.id
  )
}

/** Tri remplissage auto sans file : légendaire → étoiles → shiny incomplet → meilleures étoiles shiny → slug. */
function compareDaycareAutoPickOrder(
  a: OwnedPokemon,
  b: OwnedPokemon,
  ctx: {
    slugsWithAnyShiny: Set<string>
    slugsWithShinyFiveStars: Set<string>
    maxShinyStarsBySlug: Map<string, number>
  }
): number {
  const aLeg = (RARITY_ORDER[a.rarity] ?? 99) <= RARITY_ORDER.legendary ? 1 : 0
  const bLeg = (RARITY_ORDER[b.rarity] ?? 99) <= RARITY_ORDER.legendary ? 1 : 0
  if (aLeg !== bLeg) return bLeg - aLeg

  if (a.stars !== b.stars) return b.stars - a.stars

  const aHas = ctx.slugsWithAnyShiny.has(a.slug)
  const bHas = ctx.slugsWithAnyShiny.has(b.slug)
  const aIncomplete = aHas && !ctx.slugsWithShinyFiveStars.has(a.slug)
  const bIncomplete = bHas && !ctx.slugsWithShinyFiveStars.has(b.slug)
  if (aIncomplete !== bIncomplete) return (aIncomplete ? 1 : 0) - (bIncomplete ? 1 : 0)

  const aSh = ctx.maxShinyStarsBySlug.get(a.slug) ?? 0
  const bSh = ctx.maxShinyStarsBySlug.get(b.slug) ?? 0
  if (aSh !== bSh) return bSh - aSh

  return a.slug.localeCompare(b.slug)
}

export function getBestDaycareCandidates(
  collection: OwnedPokemon[],
  slots: DaycareSlot[],
  maxLevel: number,
  maxStars: number,
  options: DaycareCandidateOptions = {}
): OwnedPokemon[] {
  const occupied = new Set(slots.map((slot) => slotKey(slot.slug, slot.isShiny)))
  const bestBySlug = new Map<string, OwnedPokemon>()

  // Une passe O(n) : évite hasOwnedShiny* en boucle (coût catastrophique sur grosses collections).
  const slugsWithAnyShiny = new Set<string>()
  const slugsWithShinyFiveStars = new Set<string>()
  const maxShinyStarsBySlug = new Map<string, number>()
  for (const p of collection) {
    if (!p.isShiny) continue
    slugsWithAnyShiny.add(p.slug)
    if (p.stars >= maxStars) slugsWithShinyFiveStars.add(p.slug)
    maxShinyStarsBySlug.set(p.slug, Math.max(maxShinyStarsBySlug.get(p.slug) ?? 0, p.stars))
  }

  for (const pokemon of collection) {
    if (options.excludeTeamMembers && pokemon.teamSlot !== null && pokemon.teamSlot !== undefined) {
      continue
    }
    if (pokemon.level < maxLevel) continue
    if (pokemon.isShiny) continue
    if (occupied.has(slotKey(pokemon.slug, pokemon.isShiny))) continue
    if (slugsWithShinyFiveStars.has(pokemon.slug)) continue
    if (options.hideOwnedShiny && slugsWithAnyShiny.has(pokemon.slug)) continue

    const current = bestBySlug.get(pokemon.slug)
    if (!current || compareOwnedPokemon(pokemon, current) < 0) {
      bestBySlug.set(pokemon.slug, pokemon)
    }
  }

  return [...bestBySlug.values()].sort((a, b) => {
    if (options.daycareAutoPickOrder) {
      return compareDaycareAutoPickOrder(a, b, {
        slugsWithAnyShiny,
        slugsWithShinyFiveStars,
        maxShinyStarsBySlug,
      })
    }

    const aHasShiny = slugsWithAnyShiny.has(a.slug)
    const bHasShiny = slugsWithAnyShiny.has(b.slug)
    const aFive = a.stars >= maxStars
    const bFive = b.stars >= maxStars

    if (options.shinyGoal) {
      if (aFive !== bFive) return aFive ? -1 : 1
      if (aHasShiny !== bHasShiny) return aHasShiny ? 1 : -1
    } else if (aHasShiny !== bHasShiny) {
      return aHasShiny ? 1 : -1
    }

    return compareOwnedPokemon(a, b)
  })
}

export function pickInventoryCandidateForQueueEntry(
  collection: OwnedPokemon[],
  slots: DaycareSlot[],
  entry: DaycareQueueEntry,
  maxLevel: number,
  maxStars: number,
  excludedIds = new Set<number>(),
  excludeTeamMembers = false
): OwnedPokemon | null {
  if (hasOwnedShinyFiveStars(collection, entry.slug, maxStars)) return null
  if (slots.some((slot) => slot.slug === entry.slug && slot.isShiny === Boolean(entry.isShiny))) return null

  const matches = collection
    .filter((pokemon) => {
      if (excludedIds.has(pokemon.id)) return false
      if (
        excludeTeamMembers &&
        pokemon.teamSlot !== null &&
        pokemon.teamSlot !== undefined
      ) {
        return false
      }
      if (pokemon.slug !== entry.slug) return false
      if (pokemon.isShiny) return false
      if (pokemon.level < maxLevel) return false
      if (slots.some((slot) => slot.slug === pokemon.slug && slot.isShiny === pokemon.isShiny)) return false
      return true
    })
    .sort(compareOwnedPokemon)

  return matches[0] ?? null
}

export function createQueueEntryFromPokemon(pokemon: OwnedPokemon): DaycareQueueEntry {
  return {
    slug: pokemon.slug,
    nameFr: pokemon.nameFr,
    nameEn: pokemon.nameEn,
    rarity: pokemon.rarity,
    isShiny: pokemon.isShiny,
    stars: pokemon.stars,
    cosmeticFormSlug: pokemon.cosmeticFormSlug ?? null,
  }
}

export function cleanupResolvedDaycareState(
  collection: OwnedPokemon[],
  slots: DaycareSlot[],
  reserveQueue: DaycareQueueEntry[],
  lastDeposited: DaycareQueueEntry[],
  maxStars: number
) {
  const completed = new Set(
    collection
      .filter((pokemon) => pokemon.isShiny && pokemon.stars >= maxStars)
      .map((pokemon) => pokemon.slug)
  )

  if (completed.size === 0) {
    return {
      slots,
      reserveQueue,
      lastDeposited,
      removedSlots: 0,
    }
  }

  const nextSlots = slots.filter((slot) => !completed.has(slot.slug))
  const nextReserve = reserveQueue.filter((entry) => !completed.has(entry.slug))
  const nextLastDeposited = lastDeposited.filter((entry) => !completed.has(entry.slug))

  return {
    slots: nextSlots,
    reserveQueue: nextReserve,
    lastDeposited: nextLastDeposited,
    removedSlots: Math.max(0, slots.length - nextSlots.length),
  }
}
